import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import {
    Receipt as ReceiptIcon,
    OtherHouses as OtherHousesIcon,
    Sync as SyncIcon,
    Payment as PaymentIcon,
} from "@mui/icons-material";

import { BudgetCard, BudgetChart, DonutChart, DataLayout } from "../../../shared/components";
import { formatCurrency } from "../../../shared/utils";

import { useBudgetStates } from "../hooks/useBudgetState";
import { useBudgetCategories } from "../hooks/useBudgetCategories";
import { useBudgetForcase } from "../hooks/useBudgetForcase";

export const OverView: React.FC<{ projectId: string | undefined }> = ({ projectId }) => {

    const { data: budgetStatesData, isLoading: isLoadingBudgetStates } = useBudgetStates(projectId);
    const { data: budgetCategoriesData, isLoading: isLoadingBudgetCategories } = useBudgetCategories(projectId);

    const { structuredData: budgetData, isLoading: isLoadingBudgetForecast, isError, error } = useBudgetForcase(projectId);

    return (
        <Grid container spacing={2} px={2} pb={2}>

            {
                isLoadingBudgetStates ?
                    <Box display="flex" justifyContent="center" alignItems="center" width="100%" py={5}>
                        <CircularProgress />
                    </Box>
                    :
                    [
                        { label: 'Authorized Budget', value: formatCurrency(Number(budgetStatesData?.authorized_budget)).replace('£', '').trim(), icon: 'Balance', color: 'green' as const },
                        { label: 'Actual Spend', value: formatCurrency(Number(budgetStatesData?.actual_spend)).replace('£', '').trim(), icon: 'Cost', color: 'red' as const },
                        { label: 'Committed Value', value: formatCurrency(Number(budgetStatesData?.committed_value)).replace('£', '').trim(), icon: 'Assignment', color: 'info' as const },
                        { label: 'EAC Forecast', value: formatCurrency(Number(budgetStatesData?.eac_forecast)).replace('£', '').trim(), icon: 'Trending', color: 'green' as const },
                        { label: 'Pending Payments', value: formatCurrency(Number(budgetStatesData?.pending_payments)).replace('£', '').trim(), icon: 'Pending', color: 'red' as const },
                        { label: 'Accruals', value: formatCurrency(Number(budgetStatesData?.accruals)).replace('£', '').trim(), icon: 'Receipt', color: 'amber' as const },
                        { label: 'Retentions Held', value: formatCurrency(Number(budgetStatesData?.retentions_held)).replace('£', '').trim(), icon: 'Lock', color: 'green' as const },
                        { label: 'Variance %', value: `${budgetStatesData?.variance_percentage}%`, icon: 'Compare', color: 'red' as const, removeMoney: true }
                    ].map((value) => (
                        <Grid size={3}>
                            <BudgetCard label={value.label} value={value.value} icon={value.icon} color={value.color} removeMoney={value.removeMoney} />
                        </Grid>
                    ))
            }

            <Grid size={12}>
                {
                    isLoadingBudgetForecast ?
                        <Box display="flex" justifyContent="center" alignItems="center" width="100%" py={5}>
                            <CircularProgress />
                        </Box> :
                        isError ?
                            <Box p={2} display={'flex'} justifyContent={'center'} alignItems={'center'} height={300}>
                                <Typography variant="body2" color="text.secondary">
                                    Error loading budget forecast data: {error instanceof Error ? error.message : 'Unknown error'}
                                </Typography>
                            </Box> :
                            <BudgetChart budgetData={budgetData} />
                }
            </Grid>

            <Grid size={12}>
                <Box display={'flex'} alignItems={'stretch'} gap={2}>
                    <DataLayout title="Budget Categories">
                        {
                            isLoadingBudgetCategories ?
                                <Box p={2} display={'flex'} justifyContent={'center'} alignItems={'center'} height={300}>
                                    <Typography variant="body2" color="text.secondary">
                                        Loading budget categories data...
                                    </Typography>
                                </Box> :
                                budgetCategoriesData.length === 0 ? (
                                    <Box p={2} display={'flex'} justifyContent={'center'} alignItems={'center'} height={300}>
                                        <Typography variant="body2" color="text.secondary">
                                            No budget categories data available.
                                        </Typography>
                                    </Box>
                                ) :
                                    <Box height={250}>
                                        <DonutChart data={budgetCategoriesData} />
                                    </Box>
                        }
                    </DataLayout>
                    <DataLayout title="Recent Activity">
                        <Box p={2} display={'flex'} flexDirection={'column'} gap={2}>
                            {
                                [
                                    { title: 'Budget revision approved', subTitle: 'v3.2 - Enhanced fire protection • 2 hours ago', icon: 'payment', color: 'green' },
                                    { title: 'Invoice processed', subTitle: `BuildCorp - ${formatCurrency(3200000)} progress payment • 4 hours ago`, icon: 'invoice', color: 'green' },
                                    { title: 'Claim submitted (CR)', subTitle: `Weather delay extension - ${formatCurrency(125000)} • 1 day ago`, icon: 'home', color: 'green' },
                                    { title: 'F0CUS synchronization completed', subTitle: 'Data integration - all contracts updated • 1 day ago', icon: 'refresh', color: 'green' },
                                ].map((value, index) => (
                                    <Box key={index} display={'flex'} gap={2}>
                                        {
                                            value.icon === 'payment' ?
                                                <PaymentIcon sx={{ color: 'success.main' }} /> :
                                                value.icon === 'invoice' ?
                                                    <ReceiptIcon sx={{ color: 'info.main' }} /> :
                                                    value.icon === 'home' ?
                                                        <OtherHousesIcon sx={{ color: 'warning.main' }} /> :
                                                        <SyncIcon sx={{ color: 'success.main' }} />
                                        }
                                        <Box>
                                            <Typography variant="subtitle2" color="text.primary">{value.title}</Typography>
                                            <Typography variant="caption" color="text.secondary">{value.subTitle}</Typography>
                                        </Box>
                                    </Box>
                                ))
                            }
                        </Box>
                    </DataLayout>
                </Box>
            </Grid>

        </Grid>
    )
};